/**
 * Текущие значения «тюнинговых» констант PvP-рулетки.
 * По умолчанию совпадают с `pvpRoulette.ts`; в dev эмуляторе можно переопределить — см. `EmulatorPvpConstantsDrawer`.
 */

import * as C from "@/shared/constants/pvpRoulette"

export type PvpRouletteRuntimeSnapshot = {
  PENDING_TTL_MS: number
  OPTIMISTIC_TRICK_TTL_MS: number
  SECTOR_TWEEN_DURATION_MS: number
  TRICKS_SECTOR_COLOR_TWEEN_MS: number
  SPINNING_RAMP_UP_MS: number
  SPINNING_PEAK_REV_PER_SEC: number
  TRICKS_START_REV_PER_SEC: number
  TRICKS_END_REV_PER_SEC: number
  TRICKS_VISUAL_END_LEAD_MS: number
  TRICKS_DECAY_EXPONENT: number
  TRICKS_WALL_DURATION_RETUNE_MIN_DELTA_MS: number
  SLOWING_TARGET_EXP: number
  TRICK_BOOST_PEAK_DEG_PER_SEC: number
  TRICK_BOOST_DURATION_MS: number
  TRICK_FREEZE_FACTOR: number
  TRICK_FREEZE_DURATION_MS: number
  TRICK_FREEZE_MAX_PER_ROUND: number
  TRICKS_CLIENT_SCHEDULE_FALLBACK_MS: number
  INITIAL_SNAPSHOT_TIMEOUT_MS: number
  WHEEL_DEFAULT_SIZE: number
  WHEEL_MAX_SIZE: number
  WHEEL_OUTER_RADIUS_RATIO: number
  WHEEL_INNER_RADIUS_RATIO: number
  WHEEL_SECTOR_OUTER_INSET_PX: number
  WHEEL_VISUAL_SPIN_SIGN: number
  TEAM_1_COLOR: number
  TEAM_PATTERN_ICON_WIDTH: number
  TEAM_PATTERN_ICON_HEIGHT: number
  TEAM_PATTERN_GAP_X: number
  TEAM_PATTERN_GAP_Y: number
  TEAM_PATTERN_TILE_WIDTH: number
  TEAM_PATTERN_TILE_HEIGHT: number
  TEAM_PATTERN_ROTATION_DEG: number
  TEAM_PATTERN_GLOBAL_UV_ROTATION_OFFSET_DEG: number
}

function buildDefaultSnapshot(): PvpRouletteRuntimeSnapshot {
  return {
    PENDING_TTL_MS: C.PENDING_TTL_MS,
    OPTIMISTIC_TRICK_TTL_MS: C.OPTIMISTIC_TRICK_TTL_MS,
    SECTOR_TWEEN_DURATION_MS: C.SECTOR_TWEEN_DURATION_MS,
    TRICKS_SECTOR_COLOR_TWEEN_MS: C.TRICKS_SECTOR_COLOR_TWEEN_MS,
    SPINNING_RAMP_UP_MS: C.SPINNING_RAMP_UP_MS,
    SPINNING_PEAK_REV_PER_SEC: C.SPINNING_PEAK_REV_PER_SEC,
    TRICKS_START_REV_PER_SEC: C.TRICKS_START_REV_PER_SEC,
    TRICKS_END_REV_PER_SEC: C.TRICKS_END_REV_PER_SEC,
    TRICKS_VISUAL_END_LEAD_MS: C.TRICKS_VISUAL_END_LEAD_MS,
    TRICKS_DECAY_EXPONENT: C.TRICKS_DECAY_EXPONENT,
    TRICKS_WALL_DURATION_RETUNE_MIN_DELTA_MS:
      C.TRICKS_WALL_DURATION_RETUNE_MIN_DELTA_MS,
    SLOWING_TARGET_EXP: C.SLOWING_TARGET_EXP,
    TRICK_BOOST_PEAK_DEG_PER_SEC: C.TRICK_BOOST_PEAK_DEG_PER_SEC,
    TRICK_BOOST_DURATION_MS: C.TRICK_BOOST_DURATION_MS,
    TRICK_FREEZE_FACTOR: C.TRICK_FREEZE_FACTOR,
    TRICK_FREEZE_DURATION_MS: C.TRICK_FREEZE_DURATION_MS,
    TRICK_FREEZE_MAX_PER_ROUND: C.TRICK_FREEZE_MAX_PER_ROUND,
    TRICKS_CLIENT_SCHEDULE_FALLBACK_MS: C.TRICKS_CLIENT_SCHEDULE_FALLBACK_MS,
    INITIAL_SNAPSHOT_TIMEOUT_MS: C.INITIAL_SNAPSHOT_TIMEOUT_MS,
    WHEEL_DEFAULT_SIZE: C.WHEEL_DEFAULT_SIZE,
    WHEEL_MAX_SIZE: C.WHEEL_MAX_SIZE,
    WHEEL_OUTER_RADIUS_RATIO: C.WHEEL_OUTER_RADIUS_RATIO,
    WHEEL_INNER_RADIUS_RATIO: C.WHEEL_INNER_RADIUS_RATIO,
    WHEEL_SECTOR_OUTER_INSET_PX: C.WHEEL_SECTOR_OUTER_INSET_PX,
    WHEEL_VISUAL_SPIN_SIGN: C.WHEEL_VISUAL_SPIN_SIGN,
    TEAM_1_COLOR: C.TEAM_1_COLOR,
    TEAM_PATTERN_ICON_WIDTH: C.TEAM_PATTERN_ICON_WIDTH,
    TEAM_PATTERN_ICON_HEIGHT: C.TEAM_PATTERN_ICON_HEIGHT,
    TEAM_PATTERN_GAP_X: C.TEAM_PATTERN_GAP_X,
    TEAM_PATTERN_GAP_Y: C.TEAM_PATTERN_GAP_Y,
    TEAM_PATTERN_TILE_WIDTH: C.TEAM_PATTERN_TILE_WIDTH,
    TEAM_PATTERN_TILE_HEIGHT: C.TEAM_PATTERN_TILE_HEIGHT,
    TEAM_PATTERN_ROTATION_DEG: C.TEAM_PATTERN_ROTATION_DEG,
    TEAM_PATTERN_GLOBAL_UV_ROTATION_OFFSET_DEG:
      C.TEAM_PATTERN_GLOBAL_UV_ROTATION_OFFSET_DEG,
  }
}

let snapshot: PvpRouletteRuntimeSnapshot = buildDefaultSnapshot()
let revision = 0
const listeners = new Set<() => void>()

function bump() {
  revision += 1
  for (const l of listeners) l()
}

/** Текущий снимок (все числа; `WHEEL_VISUAL_SPIN_SIGN` — −1 или +1). */
export function getPvpRouletteRuntime(): Readonly<PvpRouletteRuntimeSnapshot> {
  return snapshot
}

export function getPvpRouletteRuntimeRevision(): number {
  return revision
}

export function subscribePvpRouletteRuntime(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Частичное переопределение (dev-эмулятор). В production — no-op. */
export function setPvpRouletteRuntimePatch(
  patch: Partial<PvpRouletteRuntimeSnapshot>
): void {
  if (import.meta.env.PROD) return
  snapshot = { ...snapshot, ...patch }
  bump()
}

/** Сброс к значениям из `pvpRoulette.ts`. */
export function resetPvpRouletteRuntime(): void {
  snapshot = buildDefaultSnapshot()
  bump()
}
